/*
  Castel: automatic product image sizing.
  For every product photo on a plain light background, finds where the item sits in the photo
  (the gap between its edges and the edges of the picture), then redraws it so every item fills
  the same share of the frame, centred, with equal space around it.
  - Original photos in Shopify are never changed; this only affects how they are shown.
  - Works automatically on any product, including new ones from the sync, and on cards that
    appear later (filters, "load more", quick add, variant changes).
  - Photos with busy or dark backgrounds (lifestyle shots) are left exactly as they are.
  Settings: window.CastelImageFit = { enabled: true, fill: 0.8 } in layout/theme.liquid.
*/
(function () {
  'use strict';
  var CFG = window.CastelImageFit || {};
  if (CFG.enabled === false) return;
  var FILL = Math.min(Math.max(Number(CFG.fill) || 0.8, 0.4), 0.98);
  var SELECTOR = [
    'img.product-media__image',                 // product cards, product page gallery
    'img[class*="ai-new-arrivals-image-"]',     // homepage product rows
    '.cc-co__s img'                             // style thumbnails on product page
  ].join(',');
  var ANALYSE_MAX = 160;   // analysis resolution (px)
  var OUT_MAX = 1400;      // maximum output size (px)
  var DIFF = 30;           // how different from the background a pixel must be to count as product
  var cache = new Map();   // src|aspect -> Promise<blobUrl|null>

  function isOurs(src) { return !src || src.indexOf('blob:') === 0 || src.indexOf('data:') === 0; }

  function boxAspect(img) {
    var w = img.clientWidth, h = img.clientHeight;
    if (w > 4 && h > 4) return w / h;
    var p = img.parentElement;
    for (var i = 0; i < 3 && p; i++, p = p.parentElement) {
      if (p.clientWidth > 4 && p.clientHeight > 4) return p.clientWidth / p.clientHeight;
    }
    return 1;
  }

  function load(src) {
    return new Promise(function (resolve, reject) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = function () { resolve(im); };
      im.onerror = reject;
      im.src = src;
    });
  }

  // Returns {bg:[r,g,b], x0,y0,x1,y1} in natural pixels, or null when the photo should be left alone.
  function measure(im) {
    var nw = im.naturalWidth, nh = im.naturalHeight;
    if (!nw || !nh) return null;
    var s = Math.min(1, ANALYSE_MAX / Math.max(nw, nh));
    var w = Math.max(8, Math.round(nw * s)), h = Math.max(8, Math.round(nh * s));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(im, 0, 0, w, h);
    var d = ctx.getImageData(0, 0, w, h).data; // throws if the image can't be read; caller catches

    // Background from the outer ring of pixels
    var ring = [], x, y, i;
    for (x = 0; x < w; x++) { ring.push((0 * w + x) * 4, ((h - 1) * w + x) * 4, (1 * w + x) * 4, ((h - 2) * w + x) * 4); }
    for (y = 0; y < h; y++) { ring.push((y * w) * 4, (y * w + w - 1) * 4, (y * w + 1) * 4, (y * w + w - 2) * 4); }
    var transparent = 0, rs = [], gs = [], bs = [];
    ring.forEach(function (k) {
      if (d[k + 3] < 20) { transparent++; return; }
      rs.push(d[k]); gs.push(d[k + 1]); bs.push(d[k + 2]);
    });
    var bg, isClear = transparent > ring.length * 0.6;
    if (isClear) {
      bg = [255, 255, 255];
    } else {
      var med = function (a) { a.sort(function (p, q) { return p - q; }); return a[a.length >> 1] || 0; };
      bg = [med(rs), med(gs), med(bs)];
      if ((bg[0] + bg[1] + bg[2]) / 3 < 200) return null;               // dark background: lifestyle shot
      var near = 0;
      ring.forEach(function (k) {
        if (Math.max(Math.abs(d[k] - bg[0]), Math.abs(d[k + 1] - bg[1]), Math.abs(d[k + 2] - bg[2])) <= 24) near++;
      });
      if (near < ring.length * 0.85) return null;                       // busy background: leave it
    }

    // Bounding box of the item
    var minCount = Math.max(1, Math.round(Math.min(w, h) * 0.006));
    var cols = new Uint16Array(w), rows = new Uint16Array(h);
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        i = (y * w + x) * 4;
        var hit = isClear
          ? d[i + 3] > 24
          : d[i + 3] > 24 && Math.max(Math.abs(d[i] - bg[0]), Math.abs(d[i + 1] - bg[1]), Math.abs(d[i + 2] - bg[2])) > DIFF;
        if (hit) { cols[x]++; rows[y]++; }
      }
    }
    var x0 = 0, x1 = w - 1, y0 = 0, y1 = h - 1;
    while (x0 < w && cols[x0] < minCount) x0++;
    while (x1 > x0 && cols[x1] < minCount) x1--;
    while (y0 < h && rows[y0] < minCount) y0++;
    while (y1 > y0 && rows[y1] < minCount) y1--;
    if (x0 >= x1 || y0 >= y1) return null;
    var bw = x1 - x0 + 1, bh = y1 - y0 + 1;
    if (bw * bh < w * h * 0.02) return null;                            // nothing meaningful found
    if (bw > w * 0.985 && bh > h * 0.985) return null;                  // item touches every edge: full-bleed photo
    // back to natural pixels, with a 1px (analysis) safety margin
    var k2 = 1 / s;
    return {
      bg: bg,
      x0: Math.max(0, (x0 - 1) * k2), y0: Math.max(0, (y0 - 1) * k2),
      x1: Math.min(nw, (x1 + 2) * k2), y1: Math.min(nh, (y1 + 2) * k2)
    };
  }

  function render(im, m, aspect, targetW) {
    var W = Math.round(Math.min(OUT_MAX, Math.max(320, targetW)));
    var H = Math.round(W / aspect);
    if (H > OUT_MAX) { H = OUT_MAX; W = Math.round(H * aspect); }
    var cw = m.x1 - m.x0, ch = m.y1 - m.y0;
    var scale = Math.min((W * FILL) / cw, (H * FILL) / ch);
    var dw = cw * scale, dh = ch * scale;
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d');
    ctx.fillStyle = 'rgb(' + m.bg.join(',') + ')';
    ctx.fillRect(0, 0, W, H);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(im, m.x0, m.y0, cw, ch, (W - dw) / 2, (H - dh) / 2, dw, dh);
    return new Promise(function (resolve) {
      c.toBlob(function (b) { resolve(b ? URL.createObjectURL(b) : null); }, 'image/jpeg', 0.92);
    });
  }

  function process(img) {
    if (img.dataset.ccFit === 'busy') return;
    var src = img.currentSrc || img.src;
    if (isOurs(src)) return;
    var aspect = boxAspect(img);
    var key = src + '|' + aspect.toFixed(3);
    var targetW = Math.max(img.clientWidth, 64) * Math.min(window.devicePixelRatio || 1, 2);
    img.dataset.ccFit = 'busy';
    var job = cache.get(key);
    if (!job) {
      job = load(src).then(function (im) {
        var m = measure(im);
        return m ? render(im, m, aspect, Math.max(targetW, 600)) : null;
      }).catch(function () { return null; });
      cache.set(key, job);
    }
    job.then(function (url) {
      if (!url) { img.dataset.ccFit = 'skip'; return; }
      if (!img.dataset.ccOrigSrc) img.dataset.ccOrigSrc = src;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      img.style.objectFit = 'contain';
      img.dataset.ccFit = 'done';
    });
  }

  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      var img = e.target;
      if (img.complete && img.naturalWidth) process(img);
      else img.addEventListener('load', function () { process(img); }, { once: true });
    });
  }, { rootMargin: '300px 0px' }) : null;

  function watch(img) {
    if (img.dataset.ccFitWatched === '1' && img.dataset.ccFit) return;
    img.dataset.ccFitWatched = '1';
    if (io) io.observe(img); else process(img);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SELECTOR)) watch(root);
    root.querySelectorAll(SELECTOR).forEach(watch);
  }

  function start() {
    scan(document);
    new MutationObserver(function (muts) {
      muts.forEach(function (mu) {
        if (mu.type === 'childList') {
          mu.addedNodes.forEach(function (n) { if (n.nodeType === 1) scan(n); });
        } else if (mu.type === 'attributes' && mu.target.matches && mu.target.matches(SELECTOR)) {
          // the theme swapped the photo (e.g. variant change): measure the new one
          var t = mu.target;
          if (!isOurs(t.getAttribute('src'))) {
            delete t.dataset.ccFit;
            delete t.dataset.ccOrigSrc;
            t.dataset.ccFitWatched = '';
            watch(t);
          }
        }
      });
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
